import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { BUTTONS, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { CODE_REQUIRED, EMAIL_UPDATED_SUCCESSFULLY, UPDATE_PROFILE_DESCRIPTION } from "@/utils/constants/appConstants/UpdateEmailConstants";
import AppButton from "@/components/buttons/AppButton";
import { Button } from "@/components/ui/button";
import { successToasterMessage } from "@/utils/functions/toaster";
import { getUserId, getemail_address } from "@/utils/functions/TokenFunction";
import { getEncrypted, getFromLocalStorage, setDataInLocalStorage } from "@/utils/functions/helperFunctions";
import { useLocation, useNavigate } from "react-router-dom";
import { verifyCodeAndUpdateEmail } from "@/services/user.service";

interface VerifyEmailProps {
    show?: boolean;
    onHide?: () => void;
    email: String;
    setShowEditEmailModal: (show: boolean) => void;
    setScreenState: (screenState: string) => void;
    handleResendCode: any;
    setEmailAddress: (email: string) => void;
    setIsEmailInputScreen?: (show: boolean) => void;
}

function VerifyEmailModal(props: VerifyEmailProps) {
    const [code, setCode] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errMsg, setErrMsg] = useState("");
    const isLoginWithSSO = getFromLocalStorage("loginWithSSO");
    const navigate = useNavigate();
    const location = useLocation();

    const handleUseDifferentEmail = () => {
        props.onHide?.();
        props.setShowEditEmailModal(true);
    };

    const handleVerifyAndUpdateEmail = () => {
        if (code === "") {
            setErrMsg(CODE_REQUIRED);
            return;
        }
        setIsLoading(true);
        verifyCodeAndUpdateEmail(
            getUserId() as string,
            {
                new_email: props.email,
                old_email: getemail_address() as string,
                code,
                login_via_SSO: isLoginWithSSO ? true : false,
            },
            (res: any) => {
                setIsLoading(false);
                props.onHide?.();
                navigate(location.pathname, { replace: true, state: {} });
                props.setEmailAddress(res.email_address);
                setDataInLocalStorage("email_address", getEncrypted(res.email_address));
                successToasterMessage(EMAIL_UPDATED_SUCCESSFULLY);
            },
            (err: any) => {
                setIsLoading(false);
                setErrMsg(err?.response?.data?.message);
            }
        );
    };

    const handleHideModal = () => {
        props.setIsEmailInputScreen?.(false);
        navigate(location.pathname, { replace: true, state: {} });
        props.onHide?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") handleVerifyAndUpdateEmail();
    };

    return (
        <Dialog open={props.show} onOpenChange={(open) => { if (!open) handleHideModal(); }}>
            <DialogContent className="gs-tag-dialog">
                <div className="gs-tag-dialog__header">
                    <h4 className="gs-tag-dialog__title">{HEADINGS.VERIFY_EMAIL}</h4>
                </div>

                <div className="gs-tag-dialog__body gs-update-email-dialog__body">
                    <p className="gs-update-email-message">{UPDATE_PROFILE_DESCRIPTION.VERIFY_EMAIL_MSG}</p>
                    <p className="gs-update-email-address">{props.email}</p>
                    <div className="gs-update-email-field">
                        <label htmlFor="verify-new-email-code-input" id="update-new-email-label">
                            {LABELS.VERIFICATION_CODE}
                        </label>
                        <input
                            id="verify-new-email-code-input"
                            type="text"
                            autoFocus
                            value={code}
                            maxLength={4}
                            onKeyDown={handleKeyDown}
                            placeholder="Enter 4-digit code"
                            onChange={(e) => setCode(e.target.value)}
                            className="gs-tag-dialog__input"
                        />
                        {errMsg ? <p className="gs-tag-dialog__error">{errMsg}</p> : null}
                    </div>
                    <p className="gs-update-email-resend">
                        {UPDATE_PROFILE_DESCRIPTION.DIDNOT_RECEIVE_CODE}{" "}
                        <Button variant="link" className="h-auto p-0" onClick={props.handleResendCode}>
                            {LABELS.RESEND_CODE}
                        </Button>{" "}
                        or{" "}
                        <Button variant="link" className="h-auto p-0" onClick={handleUseDifferentEmail}>
                            {UPDATE_PROFILE_DESCRIPTION.USE_DIFFERENT_EMAIL}
                        </Button>
                    </p>
                </div>

                <div className="gs-tag-dialog__footer">
                    <AppButton
                        className="gs-tag-dialog__submit btn-primary"
                        btnText={BUTTONS.VERIFY}
                        onClick={handleVerifyAndUpdateEmail}
                        isLoading={isLoading}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default VerifyEmailModal;
